import { useEffect, useRef, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "@studio-freight/lenis";
import { Toaster } from "sonner";

import { LanguageProvider } from "./context/LanguageContext";
import { CartProvider } from "./context/CartContext";

import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import CartPage from "./pages/CartPage";
import LegalPage from "./pages/LegalPage";
import ProjectPage from "./pages/ProjectPage";

import "./App.css";

// Mouse Context for subtle parallax
const MouseContext = createContext({ x: 0, y: 0 });
export const useMouse = () => useContext(MouseContext);

// Immersive 3D World Provider - Now with clean design
const Immersive3DWorld = ({ children }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      setMousePosition({
        x: (clientX - innerWidth / 2) / innerWidth,
        y: (clientY - innerHeight / 2) / innerHeight,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Trigger entry animation
    setTimeout(() => setIsLoaded(true), 100);
    
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <MouseContext.Provider value={mousePosition}>
      <div 
        className={`relative min-h-screen bg-white ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-1000`}
      >
        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </div>
    </MouseContext.Provider>
  );
};

function App() {
  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.8,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <LanguageProvider>
      <CartProvider>
        <Immersive3DWorld>
          <div className="App" data-testid="app-container">
            <BrowserRouter>
              <Header />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/legal" element={<LegalPage />} />
                <Route path="/projets/:projectSlug" element={<ProjectPage />} />
              </Routes>
              <Footer />
            </BrowserRouter>
            <Toaster 
              position="bottom-right" 
              richColors 
              toastOptions={{
                style: {
                  fontFamily: 'Manrope, sans-serif',
                },
              }}
            />
          </div>
        </Immersive3DWorld>
      </CartProvider>
    </LanguageProvider>
  );
}

export default App;
